// MRR + Churn — métricas core de un negocio de suscripción.
// MRR = ingresos de suscripciones cobrados ese mes.
// Churn = % de cancelaciones / suscriptores activos al inicio del mes.

import { useEffect, useMemo, useState } from 'react'
import AdminLayout from '../../components/AdminLayout'
import AnalisisIA from '../../components/AnalisisIA'
import { supabase } from '../../lib/supabase'

const MESES_ATRAS = 12

const fmtUYU = (n) => `$${Math.round(Number(n || 0)).toLocaleString('es-UY')}`
const ymKey = (d) => {
  const dt = new Date(d)
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`
}
const ymLabel = (ym) => {
  const [y, m] = ym.split('-')
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Set','Oct','Nov','Dic']
  return `${meses[Number(m) - 1]} ${y.slice(2)}`
}

export default function MrrChurn() {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pagos, setPagos] = useState([])
  const [clientes, setClientes] = useState([])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    setError(null)
    try {
      const desde = new Date()
      desde.setMonth(desde.getMonth() - MESES_ATRAS)
      desde.setDate(1)
      const desdeStr = desde.toISOString().slice(0, 10)

      const [{ data: pa, error: pe }, { data: cl, error: ce }] = await Promise.all([
        // MRR/Churn: solo datos reales (excluye es_prueba)
        supabase.from('pagos').select('id, monto, pagado_at, cliente_id, estado').eq('estado', 'pagado').eq('es_prueba', false).gte('pagado_at', desdeStr),
        supabase.from('clientes').select('id, estado, fecha_inicio, cancelled_at, paused_at, reactivated_at, created_at, kit_actual').eq('es_prueba', false),
      ])
      if (pe) throw pe
      if (ce) throw ce
      setPagos(pa || [])
      setClientes(cl || [])
    } catch (e) {
      setError(e.message || String(e))
    } finally {
      setLoading(false)
    }
  }

  // Agrego por mes: MRR + altas + cancelaciones + activos al fin de mes
  const filas = useMemo(() => {
    const map = new Map()
    function ensure(ym) {
      if (!map.has(ym)) map.set(ym, { ym, mrr: 0, n_pagos: 0, altas: 0, bajas: 0, activos_fin: 0 })
      return map.get(ym)
    }

    pagos.forEach(p => {
      const ym = ymKey(p.pagado_at)
      const r = ensure(ym)
      r.mrr += Number(p.monto || 0)
      r.n_pagos += 1
    })

    clientes.forEach(c => {
      if (c.fecha_inicio) {
        const ym = ymKey(c.fecha_inicio)
        ensure(ym).altas += 1
      }
      // Usamos cancelled_at real (auto-set por trigger). Fallback a created_at para clientes legacy sin trigger aplicado.
      if (c.estado === 'cancelada') {
        const fechaBaja = c.cancelled_at || c.created_at
        if (fechaBaja) ensure(ymKey(fechaBaja)).bajas += 1
      }
    })

    // Calcular activos al fin de cada mes (acumulativo desde el primero)
    const ordenadas = Array.from(map.keys()).sort()
    let activosAcum = 0
    ordenadas.forEach(ym => {
      const r = map.get(ym)
      activosAcum += r.altas - r.bajas
      r.activos_fin = activosAcum
    })

    // Calcular % churn y crecimiento MRR
    const arr = ordenadas.map(ym => map.get(ym))
    arr.forEach((r, i) => {
      const prev = i > 0 ? arr[i - 1] : null
      const baseInicio = prev?.activos_fin || 0
      r.churn_pct = baseInicio > 0 ? (r.bajas / baseInicio) * 100 : 0
      r.crecimiento_mrr_pct = prev?.mrr > 0 ? ((r.mrr - prev.mrr) / prev.mrr) * 100 : null
    })

    return arr.reverse()  // mostrar más recientes arriba
  }, [pagos, clientes])

  const ultimo = filas[0] || {}
  const anterior = filas[1] || {}

  return (
    <AdminLayout>
      <div className="page">
        <h1>📈 MRR + Churn</h1>
        <p className="muted">Ingreso recurrente mensual y tasa de cancelación. Métricas core de un modelo de suscripción.</p>

        <div className="cards-grid" style={{ marginBottom: 16 }}>
          <div className="card">
            <h3>MRR mes en curso</h3>
            <div className="stat" style={{ color: '#10B981' }}>{fmtUYU(ultimo.mrr)}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              {ultimo.crecimiento_mrr_pct != null && (
                <span style={{ color: ultimo.crecimiento_mrr_pct >= 0 ? '#10B981' : '#ef4444', fontWeight: 700 }}>
                  {ultimo.crecimiento_mrr_pct >= 0 ? '↑' : '↓'} {Math.abs(ultimo.crecimiento_mrr_pct).toFixed(1)}% vs mes anterior
                </span>
              )}
            </div>
          </div>
          <div className="card">
            <h3>Suscriptores activos</h3>
            <div className="stat">{ultimo.activos_fin ?? 0}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>al cierre del mes</div>
          </div>
          <div className="card">
            <h3>Churn del mes</h3>
            <div className="stat" style={{ color: ultimo.churn_pct > 5 ? '#ef4444' : ultimo.churn_pct > 2 ? '#f59e0b' : '#10B981' }}>
              {(ultimo.churn_pct ?? 0).toFixed(1)}%
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>{ultimo.bajas || 0} cancelaciones</div>
          </div>
          <div className="card">
            <h3>Altas del mes</h3>
            <div className="stat" style={{ color: '#3b82f6' }}>+{ultimo.altas || 0}</div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>nuevos suscriptores</div>
          </div>
        </div>

        {error && <div style={{ padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, color: '#991b1b', marginBottom: 12 }}>{error}</div>}
        {loading && <div className="ap-loading">Cargando…</div>}

        {!loading && filas.length === 0 && (
          <div className="ap-empty">
            <div style={{ fontSize: 48 }}>📭</div>
            <h3>Sin datos en los últimos {MESES_ATRAS} meses</h3>
          </div>
        )}

        {!loading && filas.length > 0 && (
          <div style={{ overflowX: 'auto', background: '#fff', borderRadius: 12, border: '1px solid #e5e7eb' }}>
            <table className="usu-table">
              <thead>
                <tr>
                  <th>Mes</th>
                  <th style={{ textAlign: 'right' }}>MRR</th>
                  <th style={{ textAlign: 'right' }}>Δ MRR</th>
                  <th style={{ textAlign: 'center' }}>+ Altas</th>
                  <th style={{ textAlign: 'center' }}>− Bajas</th>
                  <th style={{ textAlign: 'center' }}>Net</th>
                  <th style={{ textAlign: 'right' }}>Activos fin de mes</th>
                  <th style={{ textAlign: 'right' }}>Churn %</th>
                </tr>
              </thead>
              <tbody>
                {filas.map(r => {
                  const net = r.altas - r.bajas
                  return (
                    <tr key={r.ym}>
                      <td style={{ fontWeight: 700, color: '#1a3461' }}>{ymLabel(r.ym)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: '#10B981' }}>{fmtUYU(r.mrr)}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 12 }}>
                        {r.crecimiento_mrr_pct != null
                          ? <span style={{ color: r.crecimiento_mrr_pct >= 0 ? '#10B981' : '#ef4444', fontWeight: 700 }}>
                              {r.crecimiento_mrr_pct >= 0 ? '↑' : '↓'} {Math.abs(r.crecimiento_mrr_pct).toFixed(1)}%
                            </span>
                          : '—'}
                      </td>
                      <td style={{ textAlign: 'center', color: '#3b82f6', fontWeight: 700 }}>{r.altas > 0 ? `+${r.altas}` : '—'}</td>
                      <td style={{ textAlign: 'center', color: '#ef4444', fontWeight: 700 }}>{r.bajas > 0 ? `−${r.bajas}` : '—'}</td>
                      <td style={{ textAlign: 'center', fontWeight: 800, color: net > 0 ? '#10B981' : net < 0 ? '#ef4444' : '#6b7280' }}>
                        {net > 0 ? `+${net}` : net}
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 700 }}>{r.activos_fin}</td>
                      <td style={{ textAlign: 'right' }}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700,
                          background: r.churn_pct > 5 ? '#fee2e2' : r.churn_pct > 2 ? '#fef3c7' : '#d1fae5',
                          color: r.churn_pct > 5 ? '#991b1b' : r.churn_pct > 2 ? '#92400e' : '#065f46',
                        }}>
                          {r.churn_pct.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <AnalisisIA
          reportType="mrr_churn"
          data={{ ultimo_mes: ultimo, mes_anterior: anterior, serie: filas.slice(0, 6) }}
        />

        <div style={{ marginTop: 20, padding: 14, background: '#fef3c7', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 12, color: '#78350f' }}>
          <strong>📌 Notas:</strong> Churn benchmarks B2C: &lt;2% mensual = excelente, 2-5% = sano, &gt;5% = atención.
          Las bajas se contabilizan por <code>cancelled_at</code> (auto-trackeado).
        </div>
      </div>

      <style>{`
        .usu-table { width: 100%; border-collapse: collapse; font-size: 14px; }
        .usu-table thead th {
          text-align: left; padding: 12px 14px;
          background: #f8fafc; border-bottom: 1px solid #e5e7eb;
          font-size: 12px; text-transform: uppercase; color: #6b7280;
          font-weight: 700; letter-spacing: .03em;
        }
        .usu-table tbody td { padding: 12px 14px; border-bottom: 1px solid #f1f5f9; }
        .usu-table tbody tr:hover { background: #f8fafc; }
        .ap-loading { padding: 40px; text-align: center; color: #6b7280; }
        .ap-empty { padding: 60px 20px; text-align: center; color: #6b7280; }
        .ap-empty h3 { color: #1a3461; margin: 16px 0 8px; }
      `}</style>
    </AdminLayout>
  )
}
